import { Injectable, inject } from '@angular/core';
import { ContractListItem } from '@app/features/contracts/contract.model';
import { ValidationRequestTypeLabelPipe } from '@app/features/contracts/validation/validation-request-type-label-pipe';
import { DocumentTags } from '@app/features/documents/document.model';
import { ContractNotificationService } from '@app/features/notifications/contract-notification.service';
import {
  NotificationLogItem,
  NotificationOutcome,
  NotificationPerson,
  SentNotification,
  UpcomingNotification,
} from '@app/features/notifications/contract-notifications.model';
import { notificationEventLabels } from '@app/features/notifications/notification-setting.model';
import { getUserLabel } from '@app/features/users/user-label-pipe';
import { User } from '@app/features/users/user.model';
import { Observable, combineLatest, map } from 'rxjs';
import { ContractAmendments, ContractAmendmentsHistory } from './contract-amendments-history';
import {
  ContractHistorySource,
  HistoryEntry,
  HistoryLine,
  HistoryOrigin,
  HistoryTone,
} from './history-entry.model';

export const NOTIFICATION_ORIGIN: HistoryOrigin = {
  key: 'notification',
  label: 'Notification',
  note: "« Envoyé » : le service de messagerie a accepté le courriel ; rien ne dit qu'il a été délivré ni lu.",
};

/** Le sort de chaque personne désignée, dans le vocabulaire de l'émission. */
const outcomes: Record<NotificationOutcome, { label: string; tone: HistoryTone }> = {
  SENT: { label: 'envoyé', tone: 'success' },
  FAILED: { label: "échec d'envoi", tone: 'danger' },
  NO_EMAIL: { label: "écarté — pas d'adresse de courriel", tone: 'muted' },
  NOT_FOUND: { label: "écarté — introuvable dans l'annuaire", tone: 'muted' },
  AUTHOR: { label: 'auteur du geste, non notifié', tone: 'muted' },
  UNSUBSCRIBED: { label: 'retiré par son abonnement', tone: 'muted' },
};

/** Ce que compte le résumé d'un envoi ; l'auteur du geste n'y figure pas, le détail le montre. */
const summaryGroups: { outcomes: NotificationOutcome[]; one: string; many: string }[] = [
  { outcomes: ['SENT'], one: 'envoyé', many: 'envoyés' },
  { outcomes: ['FAILED'], one: 'échec', many: 'échecs' },
  { outcomes: ['NO_EMAIL', 'NOT_FOUND', 'UNSUBSCRIBED'], one: 'écarté', many: 'écartés' },
];

const ordinal = (rank: number) => (rank === 1 ? '1ᵉʳ' : `${rank}ᵉ`);

function reminder(rank: number | null, max: number | null): string | null {
  if (rank === null) return null;
  return `${ordinal(rank)} rappel${max === null ? '' : ` sur ${max}`}`;
}

function personLabel(person: NotificationPerson): string {
  // Nom et adresse figés au moment de l'envoi : le compte a pu disparaître depuis.
  return getUserLabel({
    id: person.userId,
    firstName: person.firstName ?? '',
    lastName: person.lastName ?? '',
    email: person.email ?? '',
  } as User);
}

/**
 * Les notifications du contrat : celles qui ont été envoyées, avec le sort de chaque personne
 * désignée, et les rappels programmés, qui partiront si rien ne change.
 */
@Injectable()
export class NotificationHistorySource implements ContractHistorySource {
  readonly origin = NOTIFICATION_ORIGIN;

  private readonly service = inject(ContractNotificationService);
  private readonly amendments = inject(ContractAmendmentsHistory);
  private readonly typeLabel = new ValidationRequestTypeLabelPipe();

  entries(contract: ContractListItem): Observable<HistoryEntry[]> {
    return combineLatest([this.service.list(contract.id), this.amendments.of(contract)]).pipe(
      map(([{ upcoming, sent }, amendments]) => [
        ...upcoming.map((notification, i) => this.upcoming(notification, i)),
        ...sent.map((notification) => this.sent(notification, amendments)),
      ]),
    );
  }

  private upcoming(notification: UpcomingNotification, index: number): HistoryEntry {
    const lines: HistoryLine[] = [];
    if (notification.documentTag) {
      lines.push({ text: [DocumentTags.labels[notification.documentTag]] });
    }
    lines.push(
      notification.recipients.length
        ? { text: [`à ${notification.recipients.map(personLabel).join(', ')}`] }
        : { text: ["Personne n'est désigné"], tone: 'muted' },
    );
    return {
      id: `notification-a-venir-${index}-${notification.event}-${notification.date}`,
      origin: NOTIFICATION_ORIGIN,
      at: notification.date,
      upcoming: true,
      title: [
        this.headline(notification.event, null, reminder(notification.rank, notification.max)),
      ],
      lines,
    };
  }

  private sent(notification: SentNotification, amendments: ContractAmendments): HistoryEntry {
    const items = notification.items ?? [];
    const sameReminder = new Set(items.map((item) => reminder(item.rank, item.max))).size === 1;
    const documents = items
      .filter((item) => item.documentTag)
      .map((item) => this.document(item, !sameReminder));
    const recipients = notification.recipients ?? [];
    // Une validation d'avenant notifie sur son contrat : l'entrée porte l'avenant.
    const subject = amendments.subjectOf(notification.subjectId);

    return {
      id: `notification-${notification.id}`,
      origin: NOTIFICATION_ORIGIN,
      ...(subject ? { subject } : {}),
      at: notification.date,
      title: [
        this.headline(
          notification.event,
          notification.validationType,
          sameReminder ? reminder(items[0].rank, items[0].max) : null,
        ),
      ],
      lines: [
        this.summary(notification),
        ...(documents.length ? [{ text: [documents.join(', ')] }] : []),
      ],
      details: recipients.length
        ? recipients.map((recipient) => ({
            label: [personLabel(recipient)],
            value: outcomes[recipient.outcome].label,
            tone: outcomes[recipient.outcome].tone,
          }))
        : undefined,
    };
  }

  private headline(
    event: SentNotification['event'],
    validationType: SentNotification['validationType'],
    reminderLabel: string | null,
  ): string {
    const precision = validationType ? this.typeLabel.transform(validationType) : reminderLabel;
    return precision
      ? `${notificationEventLabels[event]} — ${precision}`
      : notificationEventLabels[event];
  }

  private document(item: NotificationLogItem, withReminder: boolean): string {
    const label = DocumentTags.labels[item.documentTag!];
    const reminderLabel = withReminder ? reminder(item.rank, item.max) : null;
    return reminderLabel ? `${label} (${reminderLabel})` : label;
  }

  private summary(notification: SentNotification): HistoryLine {
    const recipients = notification.recipients ?? [];
    if (!recipients.length) {
      return { text: ["Personne n'était désigné"], tone: 'muted' };
    }
    const parts = summaryGroups
      .map((group) => ({
        group,
        count: recipients.filter((r) => group.outcomes.includes(r.outcome)).length,
      }))
      .filter(({ count }) => count > 0)
      .map(({ group, count }) => `${count} ${count === 1 ? group.one : group.many}`);
    if (!parts.length) {
      return { text: ['Aucun envoi'], tone: 'muted' };
    }
    return recipients.some((r) => r.outcome === 'FAILED')
      ? { text: [parts.join(' · ')], tone: 'danger' }
      : { text: [parts.join(' · ')] };
  }
}
